import { ref } from 'vue';
import { useStore } from 'vuex';

export function eventHasShijiBooking(event) {
  if (!event) return false;
  return !!(
    event.shiji_booking_link?.booking_id ||
    event.metadata?.shiji_booking_id ||
    event.custom_attributes?.shiji_booking_id
  );
}

export function needsCancellationReason(event, statusPayload) {
  return statusPayload?.system_stage === 'lost' && eventHasShijiBooking(event);
}

export function useLostStageUpdate() {
  const store = useStore();
  const showLostCancellationModal = ref(false);
  const pendingLostUpdate = ref(null);
  const lostCancellationSaving = ref(false);

  const requestStageUpdate = ({
    event,
    statusPayload,
    applyUpdate,
    onCancel,
  }) => {
    if (!needsCancellationReason(event, statusPayload)) {
      return applyUpdate(statusPayload);
    }

    return new Promise((resolve, reject) => {
      pendingLostUpdate.value = {
        event,
        statusPayload,
        applyUpdate,
        onCancel,
        resolve,
        reject,
      };
      showLostCancellationModal.value = true;
    });
  };

  const updateEventStage = (event, statusPayload) =>
    requestStageUpdate({
      event,
      statusPayload,
      applyUpdate: payload =>
        store.dispatch('planningEvents/update', {
          id: event.id,
          ...payload,
        }),
    });

  const confirmLostCancellation = async reason => {
    const pending = pendingLostUpdate.value;
    if (!pending || !reason?.id) return;

    lostCancellationSaving.value = true;
    try {
      const metadata = {
        ...(pending.event.metadata || {}),
        shiji_cancellation_reason_id: reason.id,
        shiji_cancellation_reason_label: reason.label || null,
      };
      const result = await pending.applyUpdate({
        ...pending.statusPayload,
        metadata,
      });
      // Clear pending before closing so dismiss/cancel from Dialog is a no-op.
      pendingLostUpdate.value = null;
      showLostCancellationModal.value = false;
      pending.resolve(result);
      return result;
    } catch (error) {
      pending.reject(error);
      return null;
    } finally {
      lostCancellationSaving.value = false;
    }
  };

  const cancelLostCancellation = () => {
    const pending = pendingLostUpdate.value;
    pendingLostUpdate.value = null;
    showLostCancellationModal.value = false;
    pending?.onCancel?.();
    pending?.resolve?.(null);
  };

  return {
    showLostCancellationModal,
    pendingLostUpdate,
    lostCancellationSaving,
    requestStageUpdate,
    updateEventStage,
    confirmLostCancellation,
    cancelLostCancellation,
  };
}
